import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Loader2, Lock, ShieldAlert } from "lucide-react";
import ergoniqLogo from "@/assets/ergoniq-logo.webp";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";

// The four aromatics chains, drawn as a process flow behind the form. Reuses the
// .flow-line keyframes already in index.css rather than adding another animation.
// Short forms are explicit, not sliced: truncating "Chlorination" to 9 characters
// renders "Chlorinat", which reads as a rendering bug rather than an abbreviation.
const CHAINS = [
  {
    label: "Benzene",
    stages: ["Chlor.", "Nitration", "Hydrog.", "Finishing"],
  },
  {
    label: "Toluene",
    stages: ["Nitration", "Hydrog.", "Granul.", "Storage"],
  },
];

function ProcessFlowMotif() {
  return (
    <svg
      viewBox="0 0 420 260"
      className="w-full h-auto"
      aria-hidden="true"
      style={{ maxWidth: 460 }}
    >
      {CHAINS.map((chain, ci) => {
        const y = 70 + ci * 110;
        return (
          <g key={chain.label}>
            <text
              x="0"
              y={y - 26}
              className="font-mono-data"
              style={{ fontSize: 9, fill: "var(--oim-text-dim)", letterSpacing: 1.5 }}
            >
              {chain.label.toUpperCase()}
            </text>
            {chain.stages.map((stage, si) => {
              const x = si * 104;
              return (
                <g key={stage}>
                  {/* Connector sits in the gap between boxes: they are 60 wide
                      on a 104 pitch, so the gap is x-44 .. x. Anything wider
                      starts inside the previous box and draws over its border. */}
                  {si > 0 && (
                    <line
                      x1={x - 40} y1={y} x2={x - 6} y2={y}
                      className="flow-line"
                      style={{ stroke: ci === 0 ? "var(--oim-green)" : "var(--oim-gold)", strokeWidth: 1.5, opacity: 0.55 }}
                    />
                  )}
                  <rect
                    x={x} y={y - 15} width={60} height={30} rx={6}
                    style={{
                      fill: "var(--oim-surface-soft)",
                      stroke: "var(--oim-border)",
                      strokeWidth: 1,
                    }}
                  />
                  <circle
                    cx={x + 10} cy={y} r={2.5}
                    style={{ fill: ci === 0 ? "var(--oim-green)" : "var(--oim-gold)" }}
                    className={si === 1 ? "pulse-glow" : undefined}
                  />
                  <text
                    x={x + 18} y={y + 3}
                    style={{ fontSize: 7.5, fill: "var(--oim-text-muted)" }}
                  >
                    {stage}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

export default function Login() {
  const { login, authEnabled, status } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const usernameRef = useRef(null);

  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  const backendDown = status === "error";

  async function onSubmit(event) {
    event.preventDefault();
    if (busy) return;
    setError("");
    if (!username.trim() || !password) {
      setError("Enter both a username and a password.");
      return;
    }
    setBusy(true);
    const result = await login(username.trim(), password);
    if (!result.ok) {
      setError(result.error);
      setPassword("");
      setBusy(false);
      return;
    }
    // On success the provider flips to "authenticated" and the router swaps this
    // screen out, so there is no need to clear busy — doing so would flash the
    // enabled button for a frame.
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: "var(--oim-bg)", color: "var(--oim-text)" }}
      data-testid="login-page"
    >
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      <div className="flex-1 grid lg:grid-cols-[1.1fr_1fr]">
        {/* Brand / context panel — hidden on small screens, where the form is all
            that matters. */}
        <div
          className="hidden lg:flex flex-col justify-between p-12"
          style={{ borderRight: "1px solid var(--oim-border)" }}
        >
          <img src={ergoniqLogo} alt="Ergoniq" className="h-8 w-auto self-start" />

          <div>
            <h1
              className="font-heading text-4xl font-bold leading-tight"
              style={{ color: "var(--oim-text)" }}
            >
              Operations
              <br />
              Intelligence
              <br />
              <span style={{ color: "var(--oim-green)" }}>Monitor</span>
            </h1>
            <p
              className="mt-5 text-sm max-w-md leading-relaxed"
              style={{ color: "var(--oim-text-muted)" }}
            >
              Live digital twins, causal root-cause analysis and margin
              intelligence across the benzene and toluene chains.
            </p>
            <div className="mt-10">
              <ProcessFlowMotif />
            </div>
          </div>

          <p
            className="font-mono-data text-[10px] uppercase tracking-widest"
            style={{ color: "var(--oim-text-dim)" }}
          >
            Aromatics · Simulation Mode
          </p>
        </div>

        {/* Form panel */}
        <div className="flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-sm">
            <div className="lg:hidden mb-8 flex items-center gap-2">
              <img src={ergoniqLogo} alt="Ergoniq" className="h-7 w-auto" />
              <span
                className="font-heading text-xs font-bold uppercase tracking-widest"
                style={{ color: "var(--oim-text-muted)" }}
              >
                OIM
              </span>
            </div>

            <h2 className="font-heading text-2xl font-bold">Sign in</h2>
            <p className="mt-1.5 text-sm" style={{ color: "var(--oim-text-muted)" }}>
              Enter your credentials to open the plant dashboard.
            </p>

            {backendDown && (
              <div
                className="mt-5 flex gap-2.5 rounded-lg p-3 text-xs"
                style={{
                  border: "1px solid rgba(255,107,0,0.4)",
                  backgroundColor: "rgba(255,107,0,0.08)",
                  color: "#FF6B00",
                }}
                data-testid="login-backend-warning"
              >
                <ShieldAlert className="w-4 h-4 shrink-0 mt-px" />
                <span>
                  The backend did not respond. Sign-in will fail until it is
                  reachable.
                </span>
              </div>
            )}

            {!authEnabled && !backendDown && (
              <div
                className="mt-5 flex gap-2.5 rounded-lg p-3 text-xs"
                style={{
                  border: "1px solid var(--oim-border)",
                  backgroundColor: "var(--oim-surface-soft)",
                  color: "var(--oim-text-muted)",
                }}
                data-testid="login-disabled-notice"
              >
                <ShieldAlert className="w-4 h-4 shrink-0 mt-px" />
                <span>
                  Login is not configured on this deployment, so no credentials
                  will be accepted. Set <code>AUTH_ADMIN_USERNAME</code> and{" "}
                  <code>AUTH_ADMIN_PASSWORD_HASH</code>.
                </span>
              </div>
            )}

            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="username" className="text-xs" style={{ color: "var(--oim-text-secondary)" }}>
                  Username
                </Label>
                <Input
                  id="username"
                  ref={usernameRef}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  disabled={busy}
                  className="bg-[var(--oim-surface)] border-[var(--oim-border)] text-[var(--oim-text)]"
                  data-testid="login-username-input"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs" style={{ color: "var(--oim-text-secondary)" }}>
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    disabled={busy}
                    className="bg-[var(--oim-surface)] border-[var(--oim-border)] text-[var(--oim-text)] pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-0 top-0 h-full px-3 flex items-center"
                    style={{ color: "var(--oim-text-dim)" }}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                    data-testid="login-password-toggle"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* aria-live so a screen reader announces the failure; the message
                  is server-supplied and never says which field was wrong. */}
              <div aria-live="polite">
                {error && (
                  <p className="text-xs" style={{ color: "#f87171" }} data-testid="login-error">
                    {error}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-[#00C853] text-black hover:bg-[#00a844] font-medium"
                data-testid="login-submit-button"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in…
                  </>
                ) : (
                  <>
                    <Lock className="w-3.5 h-3.5 mr-2" />
                    Sign in
                  </>
                )}
              </Button>
            </form>

            <p
              className="mt-8 text-[11px] leading-relaxed"
              style={{ color: "var(--oim-text-dim)" }}
            >
              Sign-in controls access to this interface. It is not a substitute for
              network-level access control on the deployment.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
